import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { cn } from "@gettactic/helpers/src/css/cn";
import { Ban, ShieldCheck, User, Users } from "lucide-react";
import React from "react";
import type {
  HoverStatus,
  HoverStatusTone
} from "@/features/map/helpers/resourceHoverStatus";

const toneText: Record<HoverStatusTone, string> = {
  green: "text-green-600",
  blue: "text-sky-600",
  amber: "text-amber-600",
  neutral: "text-neutral-500"
};

const toneDot: Record<HoverStatusTone, string> = {
  green: "bg-green-500",
  blue: "bg-sky-500",
  amber: "bg-amber-500",
  neutral: "bg-neutral-400"
};

/** Half-filled circle used for the "partially available" state. */
const HalfCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M6 1a5 5 0 0 0 0 10Z" fill="currentColor" />
  </svg>
);

const BadgeIcon: React.FC<{
  status: Extract<HoverStatus, { kind: "badge" }>;
}> = ({ status }) => {
  const iconColor = toneText[status.tone];
  switch (status.icon) {
    case "half":
      return (
        <HalfCircleIcon className={cn("h-3.5 w-3.5 shrink-0", iconColor)} />
      );
    case "shield":
      return <ShieldCheck className={cn("h-3.5 w-3.5 shrink-0", iconColor)} />;
    case "ban":
      return <Ban className={cn("h-3.5 w-3.5 shrink-0", iconColor)} />;
    case "user":
      return <User className={cn("h-3.5 w-3.5 shrink-0", iconColor)} />;
    default:
      return (
        <span
          className={cn("h-2 w-2 shrink-0 rounded-full", toneDot[status.tone])}
        />
      );
  }
};

/** The single status line of the minimal hover preview. */
export const ResourceHoverStatusLine: React.FC<{ status: HoverStatus }> = ({
  status
}) => {
  if (status.kind === "person") {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <UserAvatar user={status.user} size="xs" />
        <span className="truncate font-medium text-neutral-800 text-sm">
          {status.user.name}
        </span>
      </div>
    );
  }

  if (status.kind === "assignees") {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <Users
          className="h-3.5 w-3.5 shrink-0 text-primary"
          aria-hidden="true"
        />
        <span className="truncate font-medium text-neutral-800 text-sm">
          Multiple assignees
        </span>
        <span className="shrink-0 rounded-full bg-indigo-50 px-1.5 py-0.5 font-medium text-[11px] text-primary leading-none">
          +{status.count}
        </span>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        <BadgeIcon status={status} />
        <span
          className={cn(
            "truncate font-medium text-sm",
            status.strong ? "text-neutral-800" : toneText[status.tone]
          )}
        >
          {status.label}
        </span>
      </div>
      {status.detail ? (
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 pl-3.5 text-[11.5px] text-neutral-500">
          <span className="truncate">{status.detail.text}</span>
          {status.detail.user ? (
            <UserAvatar user={status.detail.user} size="xs" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
